export const normalizeId = (value) => String(value ?? '').trim()

export const formatDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB')
}

export const getFilteredSubjects = (subjects, studentCourseId, studentClassId) => {
  if (!studentCourseId || !studentClassId || !Array.isArray(subjects)) return []

  return subjects.filter((subjectItem) => {
    const subjectCourseId = normalizeId(
      subjectItem?.course_id ?? subjectItem?.courseId ?? subjectItem?.course?.id,
    )
    const subjectClassId = normalizeId(
      subjectItem?.class_id ??
        subjectItem?.classId ??
        subjectItem?.std_id ??
        subjectItem?.standard_id ??
        subjectItem?.class?.id,
    )

    if (!subjectCourseId) return false
    if (subjectCourseId !== studentCourseId) return false
    if (!subjectClassId) return true
    return subjectClassId === studentClassId
  })
}

/** Paper Type label from schedule `exam_type` (not subject is_theory / is_practical). */
const formatExamTypeLabel = (value) => {
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return ''
  if (raw === 'theory') return 'Theory'
  if (raw === 'practical') return 'Practical'
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

const scheduleDateAndTime = (sch) => {
  if (!sch) return { date: '', time: '' }
  const rawDate = sch?.exam_date ?? sch?.examDate ?? sch?.date ?? ''
  const start = truncateTimeForSchedule(sch?.start_time ?? sch?.startTime ?? '')
  const end = truncateTimeForSchedule(sch?.end_time ?? sch?.endTime ?? '')
  let time = start
  if (end) time = start ? `${start} – ${end}` : end
  return { date: formatDate(rawDate), time }
}

export const getSubjectRows = (filteredSubjects) =>
  filteredSubjects
    .map((item) => ({
      id: item?.id ?? item?.subject_id ?? item?.subjectId ?? null,
      name: item?.subject_name || item?.name || item?.title || '',
    }))
    .filter((item) => item.name)

export const buildExamRows = (subjectRows, exams, studentCourseId, studentClassId) => {
  if (!subjectRows.length) {
    return [
      {
        subject: 'No subjects mapped for selected course/class',
        date: '',
        time: '',
        paperType: '',
        center: 'Main Campus Hall',
      },
    ]
  }

  const normalize = (value) => String(value ?? '').trim().toLowerCase()
  const normalizedCourseId = normalize(studentCourseId)
  const normalizedClassId = normalize(studentClassId)

  const filteredExamList = Array.isArray(exams)
    ? exams.filter((examItem) => {
        const examCourseId = normalize(
          examItem?.course_id ?? examItem?.courseId ?? examItem?.course?.id,
        )
        const examClassId = normalize(
          examItem?.class_id ??
            examItem?.classId ??
            examItem?.std_id ??
            examItem?.standard_id ??
            examItem?.class?.id,
        )

        const isCourseMatched = !examCourseId || examCourseId === normalizedCourseId
        const isClassMatched = !examClassId || examClassId === normalizedClassId
        return isCourseMatched && isClassMatched
      })
    : []

  return subjectRows.map((subjectItem) => {
    const normalizedSubjectId = normalize(subjectItem.id)
    const normalizedSubjectName = normalize(subjectItem.name)

    const matchedExam = filteredExamList.find((examItem) => {
      const examSubjectId = normalize(
        examItem?.subject_id ?? examItem?.subjectId ?? examItem?.subject?.id,
      )
      const examSubjectName = normalize(
        examItem?.subject_name ??
          examItem?.subjectName ??
          examItem?.subject?.subject_name ??
          examItem?.subject?.name,
      )

      if (normalizedSubjectId && examSubjectId && examSubjectId === normalizedSubjectId) {
        return true
      }
      return normalizedSubjectName && examSubjectName === normalizedSubjectName
    })

    return {
      subject: subjectItem.name,
      date: formatDate(
        matchedExam?.exam_date ??
          matchedExam?.examDate ??
          matchedExam?.date ??
          matchedExam?.exam_datetime,
      ),
      time: matchedExam?.exam_time ?? matchedExam?.examTime ?? '',
      paperType: formatExamTypeLabel(matchedExam?.exam_type ?? matchedExam?.examType),
      center: 'Main Campus Hall',
    }
  })
}

/** HH:mm from API time string (matches ExamSubjectSchedule). */
export const truncateTimeForSchedule = (value) => {
  if (value == null || value === '') return ''
  const s = String(value).trim()
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5)
  if (/^\d{2}:\d{2}$/.test(s)) return s
  return s.length >= 5 ? s.slice(0, 5) : s
}

/**
 * Map GET /exam/subject-schedules `data` to admit-card rows in API order.
 * One row per schedule. Paper Type comes from `exam_type`, so the same subject
 * appears twice only when the API returns two schedules (theory and practical).
 */
export const mapSubjectSchedulesToAdmitRows = (scheduleList) => {
  const list = Array.isArray(scheduleList) ? scheduleList : []
  return list.map((sch) => {
    const subjectName =
      sch?.course_subject?.subject_name ||
      sch?.subject_name ||
      sch?.subjectName ||
      (sch?.course_subject_id != null ? `Subject #${sch.course_subject_id}` : '')
    const { date, time } = scheduleDateAndTime(sch)
    return {
      subject: subjectName,
      date,
      time,
      paperType: formatExamTypeLabel(sch?.exam_type ?? sch?.examType),
      center: 'Main Campus Hall',
    }
  })
}
